import { DatePicker } from "components/Form/DatePicker/DatePicker";
import { Input } from "components/Form/Input/Input";
import { Select } from "components/Form/Select/Select";
import { FeedbackReportStatus } from "generated/copilot-api-developer";
import { useTranslation } from "i18n";

export type StatusFilterOption = {
  id: string;
  label: string;
  statuses: FeedbackReportStatus[];
};

export type FeedbackReportsFilters = {
  from: ISOString | undefined;
  to: ISOString | undefined;
  feedbackId: string;
  userExternalId: string;
  statusFilter: StatusFilterOption | undefined;
};

export const FeedbackReportsSearchBar = ({
  filters,
  onChange,
  invalidDateRange,
}: {
  filters: FeedbackReportsFilters;
  onChange: (filters: FeedbackReportsFilters) => void;
  invalidDateRange: boolean;
}) => {
  const t = useTranslation();

  const update = (patch: Partial<FeedbackReportsFilters>) =>
    onChange({ ...filters, ...patch });

  const statusFilterOptions: StatusFilterOption[] = [
    {
      id: "OPEN",
      label: t("developers.feedback_reports.status_open"),
      statuses: ["OPEN", "PENDING"],
    },
    {
      id: "IN_PROGRESS",
      label: t("developers.feedback_reports.status_in_progress"),
      statuses: ["WAITING"],
    },
    {
      id: "CLOSED",
      label: t("developers.feedback_reports.status_closed"),
      statuses: ["RESOLVED", "SKIPPED", "FAILED"],
    },
  ];

  return (
    <div className="flex space-x-16 rounded shadow-sm-outlined border bg-white p-16">
      <DatePicker
        isClearable
        wrapperClassName="flex-1"
        label={t("developers.feedback_reports.from")}
        value={filters.from}
        onChange={(from) => update({ from })}
      />
      <DatePicker
        isClearable
        wrapperClassName="flex-1"
        label={t("developers.feedback_reports.to")}
        error={
          invalidDateRange
            ? t("developers.feedback_reports.invalid_date_range")
            : undefined
        }
        value={filters.to}
        onChange={(to) => update({ to })}
      />
      <Input
        wrapperClassName="flex-1"
        name="feedbackId"
        label={t("developers.feedback_reports.feedback_id")}
        value={filters.feedbackId}
        onChange={(e) => update({ feedbackId: e.currentTarget.value })}
      />
      <Input
        wrapperClassName="flex-1"
        name="userExternalId"
        label={t("developers.feedback_reports.external_user_id")}
        value={filters.userExternalId}
        onChange={(e) => update({ userExternalId: e.currentTarget.value })}
      />
      <div className="flex-1">
        <Select
          value={filters.statusFilter}
          name="status"
          label={t("developers.feedback_reports.status")}
          options={statusFilterOptions}
          getOptionId={(o) => o.id}
          getOptionLabel={(o) => o.label}
          isClearable
          onChange={(statusFilter) => update({ statusFilter })}
        />
      </div>
    </div>
  );
};
